/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { ModalAwareScrollView } from '../../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../../shared/modals/Modal';
import { Pressable, Text, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { plural } from '../../../shared/format/plural';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import type { BranchCashSessionReportRow } from '../../domain/reportsTypes'
import type { PersonalOperatorFacts, PersonalSignal } from '../../domain/reportsPersonalTypes';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { displayPaymentMethodLabel } from '../../../sales/public-api';
import { styles as reportStyles } from '../ReportsScreen.styles';
import { computePersonalActivityRates } from '../reportsScreen.utils';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  PersonalFieldRow,
  ReportsPersonalOperatorSection,
} from './personal/ReportsPersonalOperatorSection';
import {
  formatPct,
  formatTicketVs30d,
  formatTicketVsBranch,
} from './personal/personalDetailFormatters';
import { formatMoney } from '../../../shared/format/public-api';

type Props = {
  visible: boolean;
  row: PersonalOperatorFacts | null;
  globalRank: number;
  branchTicketAverage: number;
  showFiscal: boolean;
  isSingleCalendarDay: boolean;
  calendarSpanHours: number;
  branchCashSessions: BranchCashSessionReportRow[];
  operatorSignals: PersonalSignal[];
  paymentMethodNames: Record<string, string>;
  showCustomerSection: boolean;
  onClose: () => void;
};

export function ReportsPersonalOperatorDetailModal({
  visible,
  row,
  globalRank,
  branchTicketAverage,
  showFiscal,
  isSingleCalendarDay,
  calendarSpanHours,
  branchCashSessions,
  operatorSignals,
  paymentMethodNames,
  showCustomerSection,
  onClose,
}: Props) {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width, height: windowHeight } = useWindowDimensions();
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();
  const scrollMaxHeight = Math.max(360, Math.round(windowHeight * (isMobile ? 0.66 : 0.74)));

  const operatorSessions = useMemo(() => {
    if (!row?.cashierUserId) return [];
    return branchCashSessions.filter((s) => s.openedByUserId === row.cashierUserId);
  }, [branchCashSessions, row?.cashierUserId]);

  if (!row) {
    return null;
  }

  const activity = computePersonalActivityRates(row, calendarSpanHours, {
    includeActiveWindowSalesPerHour: isSingleCalendarDay,
  });

  const rankPrefix = globalRank > 0 ? `#${globalRank} · ` : '';
  const ticket30dLine = formatTicketVs30d(row.ticketAverage, row.ticketAvg30d, row.ticketVs30dPct);

  const showCommercial =
    row.salesCount > 0 ||
    row.margenReposicionTotal > 0 ||
    row.topProducts.length > 0 ||
    row.customLinePct > 0;

  const showRhythm =
    activity != null || (isSingleCalendarDay && row.timeBands.length > 0);

  const showPayments = row.paymentMix.length > 0;

  const showCash =
    row.cashClosedSessions > 0 ||
    operatorSessions.length > 0 ||
    row.cashDifferenceTotal !== 0;

  const customerNet = row.customerChargesTotal - row.customerPaymentsTotal;
  const showCustomer =
    showCustomerSection && (row.customerChargesTotal > 0 || row.customerPaymentsTotal > 0);

  const showStockControl =
    row.stockMovementCount > 0 ||
    row.shrinkageTotal > 0 ||
    row.voidedCount > 0 ||
    row.voidedAsCashierCount > 0;

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={onClose} accessibilityViewIsModal>
      <Pressable
        style={[reportStyles.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : null, isMobile ? null : modalBackdropPadding]}
        onPress={onClose}
        accessibilityLabel="Cerrar panel al tocar afuera"
      >
        <Pressable
          style={[isMobile ? reportStyles.reportsModalSheetCard : reportStyles.reportsModalDensePressable]}
          onPress={(e) => e.stopPropagation()}
          accessibilityViewIsModal
        >
          {isMobile ? <View style={reportStyles.reportsModalGrabber} /> : null}
          <ReportsDenseListCard
            wrapStyle={{ maxHeight: '88%' as const, width: '100%' as const, minWidth: 280 }}
            bodyStyle={{ paddingVertical: spacing.lg, paddingHorizontal: spacing.lg, gap: spacing.sm, ...(isMobile ? { paddingBottom: Math.max(insets.bottom, spacing.sm) } as const : {}) }}
          >
            <View style={[reportStyles.modalHeader, { alignSelf: 'stretch' }]}>
              <Text
                style={[reportStyles.modalTitle, { flex: 1, minWidth: 0, paddingRight: spacing.sm }]}
                numberOfLines={3}
              >{`${rankPrefix}${row.displayName}`}</Text>
              <Pressable onPress={onClose} accessibilityLabel="Cerrar detalle" accessibilityRole="button" hitSlop={12}>
                <Ionicons name="close" size={24} color={ticketColors.ink} />
              </Pressable>
            </View>
            <Text style={[reportStyles.caption, { alignSelf: 'stretch' }]}>
              Indicadores aproximados según ventas; no reemplazan control horario ni fichadas.
            </Text>
            <ModalAwareScrollView
              style={{ alignSelf: 'stretch', width: '100%', maxHeight: scrollMaxHeight, flexGrow: 0, flexShrink: 0 }}
              showsVerticalScrollIndicator
              contentContainerStyle={{ paddingBottom: spacing.md, width: '100%' }}
              bottomOffset={spacing.sm}
              keyboardShouldPersistTaps="handled"
            >
              {operatorSignals.length > 0 ? (
                <>
                  <Text style={[reportStyles.reportsSheetBlockTitle, { marginTop: spacing.sm }]}>Señales</Text>
                  {operatorSignals.map((s) => (
                    <Text key={s.code} style={[reportStyles.caption, { marginTop: spacing.xs }]}>
                      · {s.title}: {s.detail}
                    </Text>
                  ))}
                </>
              ) : null}

              {showCommercial ? (
                <ReportsPersonalOperatorSection
                  title="Ventas"
                  subtitle="Ventas activas del período (sin anuladas)"
                >
                  <PersonalFieldRow label="Ventas" value={String(row.salesCount)} />
                  <PersonalFieldRow
                    label="Días con ventas"
                    value={`${row.daysWithSales} / ${row.periodDays}`}
                  />
                  <PersonalFieldRow
                    label="Ticket promedio"
                    value={formatTicketVsBranch(row.ticketAverage, branchTicketAverage)}
                  />
                  {ticket30dLine ? (
                    <PersonalFieldRow label="Vs tus últimos 30 días" value={ticket30dLine} />
                  ) : null}
                  <PersonalFieldRow label="Total $" value={`${formatMoney(row.salesTotal)}`} />
                  <PersonalFieldRow label="Participación" value={formatPct(row.sharePct)} />
                  <PersonalFieldRow
                    label="Margen estimado"
                    value={`${formatMoney(row.margenReposicionTotal)} (${formatPct(row.marginPct)})`}
                  />
                  {row.topProducts.length > 0 ? (
                    <>
                      <Text style={[reportStyles.caption, { marginTop: spacing.sm, fontWeight: '600' }]}>
                        Top productos
                      </Text>
                      {row.topProducts.map((p, i) => (
                        <PersonalFieldRow
                          key={`${p.productId}-${i}`}
                          label={`${i + 1}. ${p.label}`}
                          value={`${formatMoney(p.total)} (${p.qty} u.)`}
                        />
                      ))}
                    </>
                  ) : null}
                  {row.customLinePct > 0 ? (
                    <PersonalFieldRow
                      label="Líneas sueltas / Varios"
                      value={formatPct(row.customLinePct)}
                    />
                  ) : null}
                  {showFiscal ? (
                    <>
                      <PersonalFieldRow label="Ventas fiscales" value={String(row.fiscalSalesCount)} />
                      <PersonalFieldRow label="Ventas no fiscales" value={String(row.nonFiscalSalesCount)} />
                    </>
                  ) : null}
                  <PersonalFieldRow label="Pagos pendientes" value={String(row.pendingPayments)} />
                  {row.cashierMismatchCount > 0 ? (
                    <PersonalFieldRow label="Ventas en turno ajeno" value={String(row.cashierMismatchCount)} />
                  ) : null}
                </ReportsPersonalOperatorSection>
              ) : null}

              {showRhythm ? (
                <ReportsPersonalOperatorSection
                  title="Ritmo del día"
                  subtitle={
                    isSingleCalendarDay
                      ? 'Franjas horarias y actividad'
                      : 'Seleccioná un solo día para ver las franjas'
                  }
                >
                  {activity ? (
                    <>
                      <PersonalFieldRow
                        label={
                          isSingleCalendarDay ? 'Primera venta — Última venta' : 'Primera y última venta del período'
                        }
                        value={
                          activity.hasSaleTimestamps
                            ? `${activity.firstTimeLabel} — ${activity.lastTimeLabel}`
                            : 'No disponible'
                        }
                      />
                      {activity.salesPerHourActive != null ? (
                        <PersonalFieldRow
                          label="Ventas por hora (del día)"
                          value={activity.salesPerHourActive.toFixed(1)}
                        />
                      ) : null}
                      <PersonalFieldRow
                        label="Ventas por hora (del período)"
                        value={activity.salesPerHourCalendar.toFixed(2)}
                      />
                    </>
                  ) : null}
                  {isSingleCalendarDay && row.timeBands.length > 0
                    ? row.timeBands.map((band) => (
                        <View key={band.band} style={reportStyles.row}>
                          <Text style={reportStyles.rowLabel}>{band.label}</Text>
                          <Text style={reportStyles.rowValue}>
                            {formatMoney(band.total)} ({band.count})
                          </Text>
                        </View>
                      ))
                    : null}
                </ReportsPersonalOperatorSection>
              ) : null}

              {showPayments ? (
                <ReportsPersonalOperatorSection title="Medios de cobro" subtitle="Por monto cobrado en el período">
                  {row.paymentMix.map((pm) => (
                    <PersonalFieldRow
                      key={pm.methodId}
                      label={displayPaymentMethodLabel(pm.methodId, paymentMethodNames)}
                      value={`${formatMoney(pm.total)} (${formatPct(pm.sharePct)})`}
                    />
                  ))}
                </ReportsPersonalOperatorSection>
              ) : null}

              {showCash ? (
                <ReportsPersonalOperatorSection title="Caja y turnos" subtitle="Cierres y sesiones en el período">
                  {row.cashClosedSessions > 0 ? (
                    <>
                      <PersonalFieldRow label="Cierres en período" value={String(row.cashClosedSessions)} />
                      <PersonalFieldRow
                        label="Diferencia acumulada"
                        value={`${formatMoney(row.cashDifferenceTotal)}`}
                      />
                      <PersonalFieldRow
                        label="Diferencia promedio por cierre"
                        value={`${formatMoney(row.cashAvgDifference)}`}
                      />
                      {row.cashShortageSessions > 0 ? (
                        <PersonalFieldRow
                          label="Cierres con faltante"
                          value={`${row.cashShortageSessions} (${formatPct(row.cashShortagePct)})`}
                        />
                      ) : null}
                      {row.cashAvgSessionHours > 0 ? (
                        <PersonalFieldRow
                          label="Duración promedio del turno"
                          value={`${row.cashAvgSessionHours.toFixed(1)} h`}
                        />
                      ) : null}
                    </>
                  ) : null}
                  {operatorSessions.slice(0, 8).map((session) => (
                    <View
                      key={session.id}
                      style={{
                        marginTop: spacing.sm,
                        paddingTop: spacing.sm,
                        borderTopWidth: 1,
                        borderTopColor: ticketColors.rule,
                      }}
                    >
                      <PersonalFieldRow label="Apertura" value={formatLocalDateTime(session.openedAt)} />
                      {session.closedAt ? (
                        <PersonalFieldRow label="Cierre" value={formatLocalDateTime(session.closedAt)} />
                      ) : (
                        <PersonalFieldRow label="Estado" value="Abierta" />
                      )}
                      {session.difference != null ? (
                        <PersonalFieldRow
                          label="Diferencia"
                          value={`${formatMoney(session.difference)}`}
                        />
                      ) : null}
                    </View>
                  ))}
                  {operatorSessions.length > 8 ? (
                    <Text style={reportStyles.caption}>
                      +{operatorSessions.length - 8} {plural(operatorSessions.length - 8, 'sesión', 'sesiones')} más en el período.
                    </Text>
                  ) : null}
                </ReportsPersonalOperatorSection>
              ) : null}

              {showCustomer ? (
                <ReportsPersonalOperatorSection title="Clientes" subtitle="Cargos y cobros a clientes">
                  <PersonalFieldRow
                    label="Cargos (crédito)"
                    value={`${formatMoney(row.customerChargesTotal)}`}
                  />
                  <PersonalFieldRow
                    label="Cobros"
                    value={`${formatMoney(row.customerPaymentsTotal)}`}
                  />
                  <PersonalFieldRow label="Neto período" value={`${formatMoney(customerNet)}`} />
                </ReportsPersonalOperatorSection>
              ) : null}

              {showStockControl ? (
                <ReportsPersonalOperatorSection
                  title="Stock y control"
                  subtitle="Inventario, merma y anulaciones"
                >
                  {row.stockMovementCount > 0 ? (
                    <PersonalFieldRow
                      label="Movimientos de stock"
                      value={String(row.stockMovementCount)}
                    />
                  ) : null}
                  {row.shrinkageTotal > 0 ? (
                    <PersonalFieldRow
                      label="Merma registrada"
                      value={`${formatMoney(row.shrinkageTotal)}`}
                    />
                  ) : null}
                  {row.stockNoSalesDays > 0 ? (
                    <PersonalFieldRow
                      label="Días con stock sin ventas"
                      value={String(row.stockNoSalesDays)}
                    />
                  ) : null}
                  {row.voidedCount > 0 ? (
                    <PersonalFieldRow
                      label="Anuló (como operador)"
                      value={`${row.voidedCount} ${plural(row.voidedCount, 'venta', 'ventas')} · ${formatMoney(row.voidedTotal)}`}
                    />
                  ) : null}
                  {row.voidedAsCashierCount > 0 ? (
                    <PersonalFieldRow
                      label="Le anularon (como cajero)"
                      value={`${row.voidedAsCashierCount} ${plural(row.voidedAsCashierCount, 'venta', 'ventas')} · ${formatMoney(row.voidedAsCashierTotal)}`}
                    />
                  ) : null}
                </ReportsPersonalOperatorSection>
              ) : null}
            </ModalAwareScrollView>
          </ReportsDenseListCard>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
